import type { BlockVerdict, QualityBlock } from '@/lib/contract';
import { QUALITY_BLOCKS } from '@/lib/contract';
import { cn } from '@/lib/cn';

const VERDICT_CLASS: Record<BlockVerdict, string> = {
  pass: 'bg-pass',
  warn: 'bg-warn',
  fail: 'bg-danger',
  // An unevaluated block is drawn as an empty well, not omitted - the bar is always five wide.
  unevaluated: 'bg-inset',
  not_applicable: 'bg-inset'
};

/**
 * The five-cell quality bar.
 *
 * Appears on vendor Submissions, the Datasets catalog and Dataset detail - written once.
 * Order is fixed by QUALITY_BLOCKS; a missing block renders as `unevaluated` so the bar always
 * has the same width and each position always means the same dimension.
 */
export function QualityBlocks({
  blocks,
  size = 'md',
  className
}: {
  blocks: QualityBlock[];
  size?: 'sm' | 'md';
  className?: string;
}) {
  const byKey = new Map(blocks.map(block => [block.key, block]));
  return (
    <span className={cn('inline-flex items-center gap-1', className)} aria-label="Quality blocks">
      {QUALITY_BLOCKS.map(definition => {
        const verdict = byKey.get(definition.key)?.verdict ?? 'unevaluated';
        return (
          <span
            key={definition.key}
            title={`${definition.label}: ${verdict.replace('_', ' ')}`}
            className={cn('block rounded-[2px]', size === 'sm' ? 'h-2 w-4' : 'h-2.5 w-5', VERDICT_CLASS[verdict])}
          />
        );
      })}
    </span>
  );
}

/** The numbered legend printed under the Submissions table. */
export function QualityBlocksLegend({ className }: { className?: string }) {
  return (
    <div className={cn('flex flex-wrap items-center gap-x-8 gap-y-2', className)}>
      {QUALITY_BLOCKS.map((definition, index) => (
        <span key={definition.key} className="inline-flex items-center gap-2">
          <span className="grid size-4 place-items-center rounded-[2px] bg-raised font-mono text-[9px] text-muted">
            {index + 1}
          </span>
          <span className="font-mono text-[10px] tracking-[0.12em] text-dim uppercase">{definition.label}</span>
        </span>
      ))}
      <span className="inline-flex items-center gap-2">
        <span className="block h-2.5 w-5 rounded-[2px] bg-inset" />
        <span className="font-mono text-[10px] tracking-[0.12em] text-dim uppercase">Not yet evaluated</span>
      </span>
    </div>
  );
}
